import { Link, useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import {
  Home,
  Compass,
  Search,
  Bell,
  User,
} from 'lucide-react';
import { useState } from 'react';
import { SpacesDrawer } from './spaces-drawer';

export function CommunityBottomTabs() {
  const { t } = useTranslation('community');
  const location = useLocation();
  const [spacesOpen, setSpacesOpen] = useState(false);

  const { data: unreadCount } = communityTrpc.notifications.getUnreadCount.useQuery();

  const linkItems = [
    { to: '/', icon: Home, label: t('nav.home') },
    { to: '/search', icon: Search, label: t('bottomTabs.search') },
    { to: '/notifications', icon: Bell, label: t('bottomTabs.notifications'), badge: unreadCount?.count },
    { to: '/profile', icon: User, label: t('nav.profile') },
  ];

  const homeTab = linkItems[0];
  const HomeIcon = homeTab.icon;

  return (
    <>
      <SpacesDrawer open={spacesOpen} onOpenChange={setSpacesOpen} />
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 border-t bg-card md:hidden"
        role="navigation"
        aria-label={t('bottomTabs.ariaLabel')}
      >
        <div className="flex items-center justify-around">
          {/* Home tab */}
          <Link
            to={homeTab.to}
            className={`relative flex flex-1 flex-col items-center gap-1 py-2 text-xs transition-colors ${
              location.pathname === homeTab.to ? 'text-primary' : 'text-muted-foreground'
            }`}
            aria-current={location.pathname === homeTab.to ? 'page' : undefined}
            aria-label={homeTab.label}
          >
            <HomeIcon className="h-5 w-5" />
            <span>{homeTab.label}</span>
          </Link>

          {/* Spaces tab — opens drawer instead of navigating */}
          <button
            type="button"
            onClick={() => setSpacesOpen(true)}
            className={`relative flex flex-1 flex-col items-center gap-1 py-2 text-xs transition-colors ${
              location.pathname.startsWith('/spaces') ? 'text-primary' : 'text-muted-foreground'
            }`}
            aria-label={t('nav.spaces')}
          >
            <Compass className="h-5 w-5" />
            <span>{t('nav.spaces')}</span>
          </button>

          {/* Remaining link tabs */}
          {linkItems.slice(1).map((item) => {
            const isActive = location.pathname === item.to ||
              (item.to !== '/' && location.pathname.startsWith(item.to));
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`relative flex flex-1 flex-col items-center gap-1 py-2 text-xs transition-colors ${
                  isActive ? 'text-primary' : 'text-muted-foreground'
                }`}
                aria-current={isActive ? 'page' : undefined}
                aria-label={item.label}
              >
                <div className="relative">
                  <item.icon className="h-5 w-5" />
                  {item.badge && item.badge > 0 && (
                    <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-medium text-destructive-foreground">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </div>
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>
        <div className="h-[env(safe-area-inset-bottom)] bg-card" />
      </nav>
    </>
  );
}
